import { AddressCaptureView } from '@components/AddressCapture/AddressCaptureView';
import { useAddressLookup } from '@hooks/useAddressLookup';
import type { AddressCaptureProps } from '@components/AddressCapture/AddressCapture.types';

/**
 * The address field's stable entry point. Callers pass and receive nothing
 * vendor-shaped: they hand in a placeholder and get back a `ResolvedAddress`.
 *
 * One proxied path serves both AddressProviders, so there is nothing here to
 * choose between and nothing anywhere below that branches on which one answered.
 *
 * The field mounts without first checking whether lookup can work. The old
 * pre-mount gate existed because the vendor SDK committed its credential on first
 * mount for the whole session; with the credential held by sell, this side has
 * nothing to check and the first response is the earliest anything can be known
 * to be wrong. Escalation to manual entry is raised from there, by
 * `useAddressLookup`, on either operator fault.
 */
const AddressCapture = ({
  onAddressSelected,
  defaultInputValue,
  onInputChange,
  onLookupUnavailable,
  placeholder,
  error,
}: AddressCaptureProps) => {
  const lookup = useAddressLookup({
    initialInputValue: defaultInputValue,
    onAddressSelected,
    onLookupUnavailable,
  });

  return <AddressCaptureView lookup={lookup} onInputChange={onInputChange} placeholder={placeholder} error={error} />;
};

export default AddressCapture;
